import { useEffect, useState } from "react";
import { Receipt, Download, Clock, CheckCircle2, XCircle, Landmark, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";
import { ScreenshotsPicker, BankFields, validateBank } from '@/components/BankTransferFields';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const MODES = ["NEFT", "RTGS"];
const EMPTY_FORM = { platform: "trumeet", amount_inr: "", real_name: "", account_number: "", transfer_date: "", transfer_time: "", mode: "NEFT", utr: "", bank_name: "", ifsc: "" };

export default function ResellerPayments() {
  const [orders, setOrders] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [open, setOpen] = useState(false);
  const [bank, setBank] = useState({});
  const [config, setConfig] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reseller/stock/orders").then(({ data }) => setOrders(data.orders || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!open) return;
    api.get("/reseller/bank-details").then(({ data }) => setBank(data.bank || {})).catch(() => {});
    api.get("/reseller/stock/config").then(({ data }) => setConfig(data.platforms || [])).catch(() => {});
  }, [open]);

  const price = (config.find((p) => p.key === form.platform) || {}).price || 0;
  const amt = parseFloat(form.amount_inr) || 0;
  const diamonds = price > 0 ? Math.round(amt / price) : 0;

  const submitTransfer = async () => {
    if (amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!files.length) { toast.error("Attach at least one payment screenshot"); return; }
        const bankErr = validateBank(form); if (bankErr) { toast.error(bankErr); return; }
    for (const [k, label] of [["real_name", "real name"], ["account_number", "account number"], ["transfer_date", "date"], ["utr", "UTR number"]]) {
      if (!String(form[k] || "").trim()) { toast.error(`Please enter the ${label}`); return; }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("screenshots", f));
      await api.post("/reseller/stock/bank-transfer", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Transfer submitted! ${diamonds.toLocaleString()} 💎 pending admin verification.`);
      setOpen(false); setForm(EMPTY_FORM); setFiles([]); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit");
    } finally { setSubmitting(false); }
  };

  const downloadInvoice = async (o) => {
    setDownloading(o.id);
    try {
      const res = await api.get(`/reseller/stock/invoice/${o.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock-invoice-${o.invoice_number || o.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Invoice not available yet");
    } finally { setDownloading(null); }
  };

  const totalPaid = orders.filter((o) => ["pending", "approved"].includes(o.status)).reduce((s, o) => s + (o.amount_inr || 0), 0);

  return (
    <div className="space-y-6" data-testid="reseller-payments-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Billing</p>
          <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Payments</h1>
          <p className="text-zinc-500 mt-1">Your stock refill payments and downloadable invoices.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800" data-testid="add-bank-transfer-button">
          <Landmark className="w-4 h-4 mr-2" /> Add funds via bank transfer
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard label="Total paid" value={`₹${totalPaid.toLocaleString()}`} />
        <SummaryCard label="Orders" value={orders.length.toLocaleString()} />
        <SummaryCard label="Awaiting approval" value={orders.filter((o) => o.status === "pending").length.toLocaleString()} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="reseller-payments-table">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <Th>Date</Th><Th>Platform</Th><Th className="text-right">Amount</Th>
              <Th className="text-right">Diamonds</Th><Th>Invoice #</Th><Th>Status</Th><Th className="text-right">Invoice</Th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`reseller-payment-row-${o.id}`}>
                <Td mono>{new Date(o.created_at).toLocaleDateString()}</Td>
                <Td>{o.platform_name}</Td>
                <Td className="text-right font-mono">₹{(o.amount_inr ?? 0).toLocaleString()}</Td>
                <Td className="text-right font-mono">{(o.diamonds ?? 0).toLocaleString()}</Td>
                <Td mono>{o.invoice_number || "—"}</Td>
                <Td><StatusPill status={o.status} /></Td>
                <Td className="text-right">
                  {["pending", "approved"].includes(o.status) ? (
                    <Button variant="outline" size="sm" onClick={() => downloadInvoice(o)} disabled={downloading === o.id} data-testid={`reseller-payment-invoice-${o.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" /> {downloading === o.id ? "…" : "PDF"}
                    </Button>
                  ) : <span className="text-xs text-zinc-400">—</span>}
                </Td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan="7" className="px-6 py-12 text-center text-sm text-zinc-400">No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bank-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Landmark className="w-5 h-5" /> Bank transfer refill</DialogTitle>
            <DialogDescription>Transfer funds via NEFT / RTGS, then submit the details below. Your stock is credited after admin verifies the payment.</DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex gap-2 text-[13px] text-amber-900" data-testid="bank-transfer-warning">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Your <strong>real name</strong> must match the payee/bank account name. If it doesn't match, the payment will be <strong>refunded to the source account</strong>.</span>
          </div>

          {bank.account_number && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-sm" data-testid="bank-transfer-destination">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Transfer funds to</div>
              {bank.account_name && <div><span className="text-zinc-500">Name:</span> <strong>{bank.account_name}</strong></div>}
              {bank.account_number && <div><span className="text-zinc-500">A/C:</span> <strong className="font-mono">{bank.account_number}</strong></div>}
              {bank.ifsc && <div><span className="text-zinc-500">IFSC:</span> <strong className="font-mono">{bank.ifsc}</strong></div>}
              {bank.bank_name && <div><span className="text-zinc-500">Bank:</span> {bank.bank_name}</div>}
              {bank.notes && <div className="text-xs text-zinc-500 mt-1">{bank.notes}</div>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Platform</Label>
              <Select value={form.platform} onValueChange={(v) => setForm((f) => ({ ...f, platform: v }))}>
                <SelectTrigger data-testid="bt-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{config.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount transferred (₹)</Label>
              <Input type="number" min="1" value={form.amount_inr} onChange={(e) => setForm((f) => ({ ...f, amount_inr: e.target.value }))} data-testid="bt-amount" />
            </div>
          </div>

          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center justify-between text-sm" data-testid="bt-preview">
            <span className="text-emerald-900">Stock (after approval)</span>
            <span className="font-display font-semibold text-emerald-900">{diamonds.toLocaleString()} 💎</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Your real name (as per bank)</Label>
              <Input value={form.real_name} onChange={(e) => setForm((f) => ({ ...f, real_name: e.target.value }))} data-testid="bt-real-name" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Account number (from which you paid)</Label>
              <Input value={form.account_number} onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} data-testid="bt-account-number" />
            </div>
                        <BankFields form={form} setForm={setForm} prefix="bt" />
            <div className="space-y-1.5">
              <Label className="text-xs">Transfer date</Label>
              <Input type="date" value={form.transfer_date} onChange={(e) => setForm((f) => ({ ...f, transfer_date: e.target.value }))} data-testid="bt-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Transfer time</Label>
              <Input type="time" value={form.transfer_time} onChange={(e) => setForm((f) => ({ ...f, transfer_time: e.target.value }))} data-testid="bt-time" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mode</Label>
              <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
                <SelectTrigger data-testid="bt-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">UTR / reference no.</Label>
              <Input value={form.utr} onChange={(e) => setForm((f) => ({ ...f, utr: e.target.value }))} data-testid="bt-utr" />
            </div>
          </div>

          <ScreenshotsPicker files={files} onChange={setFiles} prefix="bt" />

          <Button onClick={submitTransfer} disabled={submitting} className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="bt-submit">
            {submitting ? "Submitting…" : "Submit for verification"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-semibold tabular-nums font-mono mt-1 text-zinc-900">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { c: "bg-amber-100 text-amber-800", i: Clock, t: "Pending" },
    approved: { c: "bg-green-100 text-green-800", i: CheckCircle2, t: "Approved" },
    rejected: { c: "bg-red-100 text-red-800", i: XCircle, t: "Rejected" },
    created: { c: "bg-zinc-100 text-zinc-600", i: Clock, t: "Unpaid" },
    failed: { c: "bg-red-100 text-red-800", i: XCircle, t: "Failed" },
  };
  const m = map[status] || map.pending;
  const Icon = m.i;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${m.c}`}><Icon className="w-3 h-3" /> {m.t}</span>;
}

const Th = ({ children, className = "" }) => (<th className={`text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3 ${className}`}>{children}</th>);
const Td = ({ children, className = "", mono }) => (<td className={`px-6 py-3 text-sm text-zinc-700 ${mono ? "font-mono" : ""} ${className}`}>{children}</td>);
